/**
 * User, profile, preferences, and volunteer-role helpers.
 * Status unions are aligned with ednov-backend user-status.
 */

import { getRoleLabel } from '@/utils/role-label-utils';

/** Aligned with ednov-backend user-status: staff (`suspended` is no longer on users). */
export type StaffAccountStatus = 'active' | 'deactivated';

/** Possible `users.status` values from the API (deduped union). */
export type PrimaryContactKind = 'email' | 'phone';

/** Possible `users.status` values from the API. */
export type UserAccountStatus =
  | 'registered'
  | 'accompanied'
  | 'active'
  | 'deactivated'
  | 'archived'
  | 'suspended'
  | 'on_hold'
  | 'deleted';

/** Whether a remote user can be archived (open pairs / children). */
export interface ArchiveEligibility {
  canArchive: boolean;
  activeBinomeCount: number;
  childrenCount: number;
}

/** Body for archiving a remote user. */
export interface ArchiveRemoteUserDto {
  reason: ArchiveReason;
  comment?: string;
}

/** Result of archiving a remote user (cascaded ids). */
export interface ArchiveRemoteUserResult {
  archivedUserIds: string[];
}

/** Status / situation before and after reactivation. */
export interface ReactivationPreview {
  currentStatus: string;
  currentSituation: string | null;
  nextStatus: string;
  nextSituation: string | null;
}

/** Whether a remote user can be reactivated, plus preview. */
export interface ReactivationEligibility {
  canReactivate: boolean;
  reason?: ReactivationBlockReason;
  parentUserId?: string;
  preview?: ReactivationPreview;
}

/** Result of reactivating a remote user. */
export interface ReactivateRemoteUserResult {
  status: string;
  situation: string | null;
}
/** Stored / API profile gender. */
export type ProfileGender = 'male' | 'female' | 'other';

/** Gender select value (`''` = unset). */
export type ProfileGenderForm = ProfileGender | '';

/** How a user prefers to be contacted. */
export type ContactKind = 'email' | 'phone' | 'unknown';

/** Minimal picker row: id plus display label. */
export interface SelectedUser {
  id: string;
  label: string;
}

/** User slice rendered by {@link ParticipantChip}. */
export type ParticipantChipUser = {
  firstName: string | null;
  lastName: string | null;
  displayName?: string | null;
  photoUrl?: string | null;
};

/** Residence department (code + label) on a user profile. */
export interface ResidenceDepartment {
  id: string;
  code: string;
  name: string;
}

/** Signed-in / list user from `GET /users` (roles, mode, verification). */
export interface User {
  id: string;
  companyId: string;
  firebaseUid: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  photoUrl: string | null;
  phone: string | null;
  primaryContactKind?: PrimaryContactKind | null;
  status: UserAccountStatus;
  /** Email invite activated (password set). false while an activation link is still pending. */
  emailVerified?: boolean;
  permissions?: Record<string, string[]>;
  mode?: 'in_person' | 'remote' | 'hybrid' | null;
  userRoles?: Array<{
    roleId: string;
    role: {
      id: string;
      code: string;
      label: string;
      permissions?: Record<string, string[]>;
      roleGroup?: string;
    };
  }>;
  interfaceAccess?: Record<string, unknown>;
  userProfiles?: Array<{
    residenceDepartmentIds?: string[];
    residenceDepartments?: ResidenceDepartment[];
  }>;
  createdAt: Date;
  updatedAt: Date;
  /** Present on PATCH when the primary phone changed. */
  phoneSetupLink?: string;
}

/** Entity JSON plus optional phone-setup URL after a primary-phone change. */
export type WithPhoneSetupLink<T> = T & { phoneSetupLink?: string };

/** Editable fields for the signed-in user's own profile (PATCH /users/:id). */
export interface UpdateUserProfileDto {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  primaryContactKind?: PrimaryContactKind;
  /** Staff-only note on a remote parent, student, or tutor. Null or blank deletes it. */
  internalComment?: string | null;
}

/** Present on GET /users/students/:id when the student is deactivated (backend-aligned). */
export type StudentReactivationTargetStatus = 'registered' | 'accompanied';

/** User hydrated with profile, establishments, and computed activation flags. */
export interface UserWithProfile extends User {
  managedById?: string | null;
  hoursDone?: number;
  engagementDurationMonths?: number;
  managedBy?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  };
  currentRole?: 'tutor' | 'student' | 'volunteer' | 'coordinator';
  isPinned?: boolean;
  userProfiles?: Array<{
    id?: string;
    role?: string;
    subjects?: unknown[] | null;
    subjectNames?: string[] | null;
    availability?: Record<string, unknown> | null;
    level?: {
      id: string;
      label: string;
    };
    isMinor?: boolean | null;
    hasSpecialNeeds?: boolean | null;
    vscReportsFrequencies?: {
      daily: boolean;
      weekly: boolean;
      monthly: boolean;
    };
    dateOfBirth?: string | null;
    gender?: 'male' | 'female' | 'other' | null;
    address?: string | null;
    levelId?: string | null;
    medicalNotes?: string | null;

    engagementStartDate?: Date | null;
    engagementEndDate?: Date | null;
    /** Tutor profile — English enum from API */
    professionalSituation?: 'employee' | 'student' | 'retired' | null;
    /** Tutor objective — number of hours to achieve */
    targetHours?: number | null;
    /** Coordinator annual establishment prospection goal */
    annualProspectionGoal?: number | null;
    /** Residence departments (manager/coordinator scope) */
    residenceDepartmentIds?: string[];
    residenceDepartments?: ResidenceDepartment[];
    /** Department UUID FK for remote admin roles */
    departmentId?: string | null;
    /** Admin-configured: may this tutor add/create students on their sessions (mobile)? */
    canAddStudentsToSession?: boolean;
    /** Admin-configured: may this tutor remove students from their sessions (mobile)? */
    canRemoveStudentsFromSession?: boolean;

    userProfileSpecialties?: Array<{
      specialty: {
        id: string;
        code: string;
        label: string;
      };
    }> | null;
  }>;
  userRoles?: Array<{
    roleId: string;
    role: {
      id: string;
      code: string;
      label: string;
      roleGroup?: string;
    };
  }>;
  userEstablishments?: Array<{
    id: string;
    establishmentId: string;
    establishment: {
      id: string;
      name: string;
      uaiCode: string | null;
      type: string;
      city: string;
      postalCode: string;
    };
  }>;
  /**
   * Email-invited account still awaiting activation (computed from Firebase on the detail
   * endpoints). Drives the regenerate-activation-link button. Undefined on list payloads.
   */
  pendingActivation?: boolean;
  /** Program IDs for coordinator_remote (loaded from user_programs on admin detail endpoint). */
  programIds?: string[];
  /** Session attendances (deactivated student, enriched payload). */
  presentSessionCount?: number;
  /** Reactivation target from attendance count (deactivated student, enriched payload). */
  reactivationTargetStatus?: StudentReactivationTargetStatus;
}

/** React Query page for `['users-search', role]` caches. */
export type UsersSearchPage = {
  data: UserWithProfile[];
  total: number;
  hasMore: boolean;
};

/** One weekly profile slot (`day` 0–6). Distinct from matching-remote {@link AvailabilitySlot}. */
export interface WeeklyAvailabilitySlot {
  day: number;
  start: string;
  end: string;
}

/** Criminal-record validation action. */
export type CriminalRecordDecisionAction = 'validate' | 'refuse' | 'cancel';

/** Criminal-record file from `GET /users/:userId/criminal-record`. */
export interface CriminalRecordDto {
  fileName: string;
  depositedAt: string;
  status: 'pending' | 'validated' | 'rejected';
  validatedAt: string | null;
  validatedByFullName: string | null;
  rejectedAt: string | null;
  rejectedByFullName: string | null;
}

/** Metadata returned after `POST /users/:userId/criminal-record`. */
export interface CriminalRecordUploadInfo {
  fileName: string;
  depositedAt: string;
  status: 'pending' | 'validated';
  validatedAt: string | null;
}

/** `GET /learning360/tutors/:userId/path-stats` payload. */
export interface Learning360TutorPathStats {
  state: 'not_started' | 'in_progress' | 'unavailable';
  progress: number | null;
  timeSpentMs: number | null;
  startedAt: string | null;
  completedAt: string | null;
  status: string | null;
  score: number | null;
}

/** Shared list/export filters for staff user endpoints. */
export interface GetUsersParams {
  search?: string;
  page?: number;
  pageSize?: number;
  excludeIds?: string[];
  statuses?: string[];
  establishmentId?: string;
  programIds?: string[];
  departmentCodes?: string[];
  subjectIds?: string[];
  /** 0=Sunday .. 6=Saturday (OR: user has a slot on any of these days) */
  availabilityDays?: number[];
  /**
   * Precise availability filter — all three must be provided together.
   * Filters users who have a slot overlapping [start, end] on that day of week.
   * When set, takes precedence over availabilityDays.
   */
  availabilityDayOfWeek?: number;
  availabilityStartTime?: string;
  availabilityEndTime?: string;
  /** VSC report frequency filter (OR within group) */
  vscFrequencies?: ('daily' | 'weekly')[];
  levelIds?: string[];
  classIds?: string[];
  specialtyIds?: string[];
  roleCodes?: string[];
  /** Filter /organizers by role group (e.g. ['tutors', 'volunteers'] for session facilitators) */
  roleGroups?: string[];
  dateFrom?: Date;
  dateTo?: Date;
  managedById?: string;
  programName?: string;
  isMinor?: boolean;
  hasSpecialNeeds?: boolean;
  accompanimentTypes?: ('collective' | 'individual')[];
  exitDateFrom?: Date;
  exitDateTo?: Date;
  exitReason?: string;
  /** Engagement start date range (ISO string) — for tutors/volunteers */
  engagementStartDateFrom?: string;
  engagementStartDateTo?: string;
  /** Engagement end date range (ISO string) — for volunteers */
  engagementEndDateFrom?: string;
  engagementEndDateTo?: string;
  /** Exit date range (ISO string) — for tutors/volunteers */
  tutorExitDateFrom?: string;
  tutorExitDateTo?: string;
  tutorExitReason?: string;
}

/** `POST /users/invite` body for an onboarding prospect. */
export interface InviteProspectDto {
  email?: string;
  phone?: string;
  firstName: string;
  lastName: string;
  roleCode: 'tutor_remote' | 'student_remote' | 'parent_remote';
  sessionId?: string;
}

/** Remote onboarding pipeline statuses. */
export type OnboardingStatus = 'prospect' | 'registered' | 'confirmed' | 'active';

/** Folder completeness (`incomplete` → `complete`). */
export type FolderSituation = 'incomplete' | 'partial' | 'in_validation' | 'complete';

/** Lifecycle step on a remote / in-person onboarding record. */
export type CycleStep = 'collection' | 'acquisition' | 'activation' | 'active';

/** Pre- vs post-registration onboarding phase. */
export type Phase = 'pre_registration' | 'post_registration';

/** Per-step progress on the remote onboarding wizard. */
export type StepStatus = 'locked' | 'pending' | 'in_progress' | 'completed';

/** Section-level completion rollup on the record. */
export interface GlobalCompletion {
  totalSections: number;
  completedSections: number;
  completedSectionsPercentage: number;
}

/** Staff validation stamp (`validatedBy` / `validatedAt` / comments). */
export interface ValidationBlock {
  isValidated: boolean;
  validatedBy: { userId: string; userFullName: string; userEmail: string } | null;
  validatedAt: string | null;
  comments: string | null;
}

/** Statuses that can be reactivated (`archived` / `suspended`). */
export const REACTIVATABLE_USER_STATUSES = ['archived', 'suspended'] as const;

/** One value from {@link REACTIVATABLE_USER_STATUSES}. */
export type ReactivatableUserStatus = (typeof REACTIVATABLE_USER_STATUSES)[number];

/** True when `status` is archived or suspended. */
export function isReactivatableUserStatus(
  status: string | undefined | null,
): status is ReactivatableUserStatus {
  return (
    typeof status === 'string' &&
    (REACTIVATABLE_USER_STATUSES as readonly string[]).includes(status)
  );
}

/** Remote archive-reason codes. */
export const ARCHIVE_REASONS = [
  'no_follow_up',
  'disengaged',
  'pause',
  'duplicate',
  'other',
] as const;

/** One remote archive-reason code. */
export type ArchiveReason = (typeof ARCHIVE_REASONS)[number];

/** Reasons a remote user cannot be reactivated. */
export const REACTIVATION_BLOCK_REASONS = [
  'not_reactivatable',
  'no_parent',
  'parent_not_active',
  'parent_reactivatable',
] as const;

/** One reactivation-block reason code. */
export type ReactivationBlockReason = (typeof REACTIVATION_BLOCK_REASONS)[number];

/** Legacy / hybrid payloads → canonical staff `active` | `deactivated`. */
export function normalizeStaffAccountStatus(raw: string): StaffAccountStatus {
  if (raw === 'active' || raw === 'deactivated') return raw;
  if (raw === 'suspended') return 'deactivated';
  if (raw === 'archived' || raw === 'on_hold' || raw === 'deleted') return 'deactivated';
  return 'active';
}

/**
 * `user_profiles.gender` is stored on the backend as a single-letter code
 * (`M` | `F` | `O`, see `ednov-backend/src/common/utils/gender.utils.ts`), but edit
 * form Selects use the full words. Without this mapping, edit forms load with an
 * empty (invalid) gender field and the required-field check keeps Save
 * disabled until the user manually reselects a gender.
 */

/** Stored gender letter / word → form select value. */
export function normalizeProfileGender(raw: string | null | undefined): ProfileGenderForm {
  if (!raw) return '';
  const g = String(raw).trim().toUpperCase();
  if (g === 'M' || g === 'MALE') return 'male';
  if (g === 'F' || g === 'FEMALE') return 'female';
  if (g === 'O' || g === 'OTHER') return 'other';
  return '';
}

/** Known `volunteers` group codes — fallback when the API omits `roleGroup`. */
export const VOLUNTEER_ROLE_CODES = ['volunteer', 'vsc', 'professor'] as const;

/** VSC codes that can observe a session without an assigned student. */
export const VSC_OBSERVER_ROLE_CODES = ['volunteer', 'vsc'] as const;

/** Professor code: must be linked to a student the same way as a tutor. */
export const PROFESSOR_ROLE_CODES = ['professor'] as const;

/** Lowercases a role code, treating null/undefined as empty. */
function normalizeRoleCode(code: string | null | undefined): string {
  return (code ?? '').toLowerCase();
}

/** True when any assigned role is a professor (must be linked like a tutor). */
export function isProfessorUser(user: Pick<UserWithProfile, 'userRoles'>): boolean {
  for (const ur of user.userRoles ?? []) {
    const code = normalizeRoleCode(ur.role?.code);
    if ((PROFESSOR_ROLE_CODES as readonly string[]).includes(code)) return true;
  }
  return false;
}

/** VSC (`volunteer` / `vsc`) who can sit in as an observer without a student. */
export function isVscObserverUser(
  user: Pick<UserWithProfile, 'userRoles' | 'currentRole'>,
): boolean {
  if (isProfessorUser(user)) return false;
  for (const ur of user.userRoles ?? []) {
    const code = normalizeRoleCode(ur.role?.code);
    if ((VSC_OBSERVER_ROLE_CODES as readonly string[]).includes(code)) return true;
  }
  // Prefill participants without userRoles: only the session `volunteer` role marks a VSC.
  return user.currentRole === 'volunteer';
}

/**
 * Specific volunteer role label (e.g. "Volunteer" for `volunteer`, "Professeur" for `PROFESSOR`).
 * Uses `roleGroup` when present, otherwise known codes.
 */
export function getVolunteerRoleLabel(user: Pick<UserWithProfile, 'userRoles'>): string | null {
  if (!user?.userRoles?.length) return null;
  for (const ur of user.userRoles) {
    const isVolunteer =
      ur.role.roleGroup === 'volunteers' ||
      VOLUNTEER_ROLE_CODES.includes(
        ur.role.code.toLowerCase() as (typeof VOLUNTEER_ROLE_CODES)[number],
      );
    if (isVolunteer) return getRoleLabel(ur.role.code);
  }
  return null;
}

/** True when the user belongs to the `volunteers` group. */
export function isVolunteerUser(user: Pick<UserWithProfile, 'userRoles'>): boolean {
  return getVolunteerRoleLabel(user) !== null;
}

/** First `roleGroup` found on the user, or null. */
export function getUserRoleGroup(user: Pick<UserWithProfile, 'userRoles'>): string | null {
  if (!user?.userRoles?.length) return null;
  const roleWithGroup = user.userRoles.find((ur) => ur.role.roleGroup);
  return roleWithGroup?.role.roleGroup || null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Criminal record
// ─────────────────────────────────────────────────────────────────────────────

// Shared remote / in-person onboarding (identical across parent, student-remote, tutor-remote, tutor-inperson)
